import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, isConfigured } from '../lib/supabase';
import { ROLE_PRESETS, normalizePermissions, canAccessModule } from '../lib/permissions';
import { getDemoAccountByEmail } from '../lib/demoAccounts';

import { decidirAccesoDeCliente, decidirAccesoDeClienteConMembresia } from '../lib/clientAccess';
import { markPasswordRecovery, clearPasswordSetupIntent } from '../lib/recoveryIntent';
import { SELECT_SEGURO_PARA_EL_CLIENTE } from '../lib/columnasDeCliente';
const AuthContext = createContext(null);
const LOCAL_DEMO_AUTH_KEY = 'feria_demo_user_email';
const USE_LOCAL_DEMO_AUTH = process.env.NODE_ENV !== 'production' && (process.env.NODE_ENV === 'test' || process.env.REACT_APP_USE_SEED === 'true' || !isConfigured);
const PROFILE_QUERY_TIMEOUT_MS = 4500;
const PROFILE_RETRY_DELAYS = [0, 700, 1600];
const PROFILE_CACHE_PREFIX = 'feria_confirmed_profile_v1';
const PROFILE_CACHE_TTL_MS = 10 * 60 * 1000;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// ── COLORES POR USUARIO (solo presentación — no define permisos) ──
const USER_COLORS = {
  'david@feriadesign.com':  { color: '#C9A96E', initials: 'DA', name: 'David',  roleLabel: 'Director'          },
};

function createDemoUser(account) {
  const role = account.role || 'cliente';
  return {
    id: `demo-${account.id}`,
    aud: 'authenticated',
    email: account.email,
    user_metadata: { name: account.name },
    name: account.name,
    role,
    perms: role,
    permissions: normalizePermissions(role),
    roleLabel: ROLE_PRESETS[role]?.label || account.roleLabel,
    color: account.color,
    initials: account.name.slice(0, 2).toUpperCase(),
    isClient: role === 'cliente',
    clientEmail: role === 'cliente' ? account.email : null,
    _source: 'local-demo',
  };
}

function createProfileLoadErrorUser(supabaseUser, cosmetic = {}, source = 'profile-error', message = 'No pudimos cargar tu perfil.') {
  const email = supabaseUser?.email || '';
  return {
    ...supabaseUser,
    name: cosmetic.name || supabaseUser?.user_metadata?.name || email.split('@')[0] || 'Usuario',
    role: 'profile_error',
    perms: 'profile_error',
    permissions: normalizePermissions('profile_error'),
    roleLabel: 'Perfil no disponible',
    color: cosmetic.color || '#5B9BD5',
    initials: cosmetic.initials || email.slice(0, 2).toUpperCase() || 'US',
    isClient: false,
    clientEmail: null,
    profileLoadError: true,
    profileErrorMessage: message,
    _source: source,
  };
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function createProfileTimeoutError(message) {
  const error = new Error(message);
  error.name = 'TimeoutError';
  return error;
}

async function runProfileQueryWithTimeout(query, message) {
  let timeoutId;
  let controller = null;
  let request = query;

  if (typeof AbortController !== 'undefined' && typeof query?.abortSignal === 'function') {
    controller = new AbortController();
    request = query.abortSignal(controller.signal);
  }

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      if (controller) controller.abort();
      reject(createProfileTimeoutError(message));
    }, PROFILE_QUERY_TIMEOUT_MS);
  });

  try {
    return await Promise.race([request, timeoutPromise]);
  } catch (error) {
    if (error?.name === 'AbortError') throw createProfileTimeoutError(message);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function runProfileFetchWithTimeout(url, accessToken, message) {
  if (!SUPABASE_URL || !SUPABASE_ANON || !accessToken || typeof fetch !== 'function') {
    return { data: null, error: new Error('profile rest unavailable') };
  }

  let timeoutId;
  let controller = null;
  if (typeof AbortController !== 'undefined') controller = new AbortController();

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      if (controller) controller.abort();
      reject(createProfileTimeoutError(message));
    }, PROFILE_QUERY_TIMEOUT_MS);
  });

  const request = fetch(url, {
    headers: {
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
    signal: controller?.signal,
  });

  try {
    const response = await Promise.race([request, timeoutPromise]);
    const text = await response.text();
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
    if (!response.ok) {
      return { data: null, error: new Error(payload?.message || payload?.error || `profile rest ${response.status}`) };
    }
    return { data: Array.isArray(payload) ? payload[0] || null : payload, error: null };
  } catch (error) {
    if (error?.name === 'AbortError') return { data: null, error: createProfileTimeoutError(message) };
    return { data: null, error };
  } finally {
    clearTimeout(timeoutId);
  }
}

function profileCacheKey(userId) {
  return `${PROFILE_CACHE_PREFIX}:${userId}`;
}

function storeConfirmedProfile(supabaseUser, profileUser) {
  if (typeof window === 'undefined' || !supabaseUser?.id || profileUser?.profileLoadError) return;
  try {
    const safeProfile = {
      auth_id: supabaseUser.id,
      email: supabaseUser.email || '',
      name: profileUser.name,
      role: profileUser.role,
      perms: profileUser.perms,
      permissions: profileUser.permissions,
      roleLabel: profileUser.roleLabel,
      color: profileUser.color,
      initials: profileUser.initials,
      isClient: profileUser.isClient,
      clientEmail: profileUser.clientEmail,
      cached_at: Date.now(),
    };
    window.sessionStorage.setItem(profileCacheKey(supabaseUser.id), JSON.stringify(safeProfile));
  } catch {
    // Cache is only a stability aid; failing to write it must not affect auth.
  }
}

function readConfirmedProfileCache(supabaseUser) {
  if (typeof window === 'undefined' || !supabaseUser?.id) return null;
  try {
    const raw = window.sessionStorage.getItem(profileCacheKey(supabaseUser.id));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    const sameUser = cached?.auth_id === supabaseUser.id && String(cached?.email || '').toLowerCase() === String(supabaseUser.email || '').toLowerCase();
    const fresh = Date.now() - Number(cached?.cached_at || 0) < PROFILE_CACHE_TTL_MS;
    if (!sameUser || !fresh || !cached?.role) return null;
    return {
      ...supabaseUser,
      name: cached.name || supabaseUser?.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'Usuario',
      role: cached.role,
      perms: cached.perms || cached.role,
      permissions: normalizePermissions(cached.role, cached.permissions),
      roleLabel: cached.roleLabel || ROLE_PRESETS[cached.role]?.label || 'Colaborador',
      color: cached.color || '#5B9BD5',
      initials: cached.initials || supabaseUser.email?.slice(0, 2).toUpperCase() || 'US',
      isClient: cached.role === 'cliente' || cached.isClient === true,
      clientEmail: cached.role === 'cliente' ? supabaseUser.email : null,
      profileRefreshing: true,
      _source: 'profile-cache',
    };
  } catch {
    return null;
  }
}

function isConfirmedOperationalUser(candidate, supabaseUser) {
  if (!candidate || candidate.profileLoadError || candidate.role === 'profile_error') return false;
  const sameId = candidate.id && supabaseUser?.id && candidate.id === supabaseUser.id;
  const sameEmail = String(candidate.email || '').toLowerCase() === String(supabaseUser?.email || '').toLowerCase();
  return Boolean(sameId || sameEmail);
}

function resolveStableProfileResult(profile, supabaseUser, currentUser) {
  if (!profile?.profileLoadError) return profile;
  if (isConfirmedOperationalUser(currentUser, supabaseUser)) {
    console.warn('[AuthContext] Profile refresh failed; preserving last confirmed role.', {
      source: profile._source,
      message: profile.profileErrorMessage,
    });
    return {
      ...currentUser,
      profileRefreshing: true,
      profileRefreshWarning: profile.profileErrorMessage,
    };
  }

  const cached = readConfirmedProfileCache(supabaseUser);
  if (cached) {
    console.warn('[AuthContext] Profile refresh failed; using short-lived confirmed profile cache.', {
      source: profile._source,
      message: profile.profileErrorMessage,
    });
    return {
      ...cached,
      profileRefreshWarning: profile.profileErrorMessage,
    };
  }

  return profile;
}

async function readProfileWithTimeout(userId, email, accessToken = '') {
  const select = 'id,role,name,email,permissions';
  const emailValue = String(email || '').trim().toLowerCase();

  if (accessToken && SUPABASE_URL && SUPABASE_ANON) {
    const byRestId = await runProfileFetchWithTimeout(
      `${SUPABASE_URL}/rest/v1/profiles?select=${encodeURIComponent(select)}&id=eq.${encodeURIComponent(userId)}&limit=1`,
      accessToken,
      'profiles rest timeout'
    );
    if (!byRestId.error && byRestId.data) return byRestId;

    if (emailValue) {
      const byRestEmail = await runProfileFetchWithTimeout(
        `${SUPABASE_URL}/rest/v1/profiles?select=${encodeURIComponent(select)}&email=ilike.${encodeURIComponent(emailValue)}&limit=1`,
        accessToken,
        'profiles rest email timeout'
      );
      if (!byRestEmail.error && byRestEmail.data) return byRestEmail;
      if (byRestEmail.error) return byRestEmail;
    }
    if (byRestId.error) return byRestId;
  }

  const byId = await runProfileQueryWithTimeout(
    supabase
    .from('profiles')
    .select('id, role, name, email, permissions')
    .eq('id', userId)
      .maybeSingle(),
    'profiles timeout'
  );

  if (!byId.error && byId.data) return byId;

  if (!emailValue) return byId;

  const byEmail = await runProfileQueryWithTimeout(
    supabase
    .from('profiles')
    .select('id, role, name, email, permissions')
    .ilike('email', emailValue)
      .maybeSingle(),
    'profiles email timeout'
  );

  return (!byEmail.error && byEmail.data) ? byEmail : byId;
}

// ── LA LISTA ES EXPLICITA, Y ESO NO ES ESTILO ───────────────────────────────
//
// Esta ficha viaja al navegador del CLIENTE. `clientes` tiene 38 columnas y el
// portal consume 17; las otras 21 son operacion del estudio: `notas`,
// `acquisition_notes`, `acquisition_confidence`, `stage`, `creativo_id`,
// `archive_reason`, `sector`, `email_declarado_evidencia`... Con `select=*` todas
// cruzaban la red sin que nadie las pidiera, y RLS no lo impide: gobierna QUE FILAS
// se ven, no que columnas.
//
// Las 19 de abajo son las 17 que el portal lee de verdad —medido sobre los 43
// ficheros de `ClientPortal` y `features/client-portal`— mas `archived_at` y
// `portal_password_must_change`, que el portal no pinta pero necesita la decision de
// acceso de aqui mismo: `decidirAccesoDeCliente` lee `archived_at` y
// `portal_access_status`.
//
// Si el portal empieza a necesitar otra columna, se anade aqui a proposito. Volver a
// `*` seria regalar la operacion interna del estudio a cambio de no escribir una lista.
// La lista vivia aqui y ahora vive en `lib/columnasDeCliente.js`, importada arriba:
// `db.clientes.getAll()` necesitaba la MISMA, y dos copias de una decision de
// privacidad son dos decisiones que divergen el dia que alguien toca una.
const COLUMNAS_DE_LA_FICHA_DEL_PORTAL = SELECT_SEGURO_PARA_EL_CLIENTE;

async function readClientPortalIdentityWithTimeout(supabaseUser, accessToken = '') {
  if (!supabaseUser?.id || !accessToken || !SUPABASE_URL || !SUPABASE_ANON) return { data: null, error: null };
  return runProfileFetchWithTimeout(
    `${SUPABASE_URL}/rest/v1/clientes?select=${COLUMNAS_DE_LA_FICHA_DEL_PORTAL}&portal_user_id=eq.${encodeURIComponent(supabaseUser.id)}&limit=1`,
    accessToken,
    'client portal identity timeout'
  );
}

// El participante (cofirmante/invitado) NO figura en `clientes.portal_user_id`: llega por su
// membresia. RLS ya lo acota — `client_portal_members_select_self` le deja ver su propia fila
// activa, y `owns_cliente` la ficha embebida — asi que el filtro explicito es intencion, no
// seguridad.
async function readClientPortalIdentityByMembershipWithTimeout(supabaseUser, accessToken = '') {
  if (!supabaseUser?.id || !accessToken || !SUPABASE_URL || !SUPABASE_ANON) return { data: null, error: null };
  return runProfileFetchWithTimeout(
    `${SUPABASE_URL}/rest/v1/client_portal_members?user_id=eq.${encodeURIComponent(supabaseUser.id)}&status=eq.active&select=cliente_id,clientes(id,archived_at,portal_access_status)&limit=1`,
    accessToken,
    'client portal membership timeout'
  );
}

function createClientPortalUserFromCliente(supabaseUser, cliente, cosmetic = {}, source = 'client-portal-fallback') {
  // Misma decision que usa la rama del perfil presente: una sola fuente, para que no
  // vuelvan a divergir (era la duplicacion la que dejaba media app sin proteger).
  if (decidirAccesoDeCliente(cliente) === 'bloqueado') {
    return createProfileLoadErrorUser(
      supabaseUser,
      cosmetic,
      'client-access-blocked',
      'Tu acceso al portal no esta activo. Escríbenos para revisar tu cuenta.'
    );
  }

  const permissions = { modules: ['client_portal'], brands: ['feria', 'bl'] };
  const resolved = {
    ...supabaseUser,
    name: cliente?.nombre || cosmetic.name || supabaseUser?.user_metadata?.name || supabaseUser?.email?.split('@')[0] || 'Cliente',
    role: 'cliente',
    perms: 'cliente',
    permissions: normalizePermissions('cliente', permissions),
    roleLabel: ROLE_PRESETS.cliente?.label || 'Cliente',
    color: cosmetic.color || '#5B9BD5',
    initials: (cliente?.nombre || supabaseUser?.email || 'CL').slice(0, 2).toUpperCase(),
    isClient: true,
    clientEmail: supabaseUser?.email || cliente?.email || null,
    // La misma identidad que la rama del perfil presente: una sola forma de
    // responder «cual es mi ficha», para que las dos ramas no vuelvan a divergir.
    clienteDelPortal: cliente || null,
    portalPasswordMustChange: cliente?.portal_password_must_change === true,
    _source: source,
  };
  storeConfirmedProfile(supabaseUser, resolved);
  return resolved;
}

// ── CONSTRUIR PERFIL DE USUARIO ───────────────────────────────────
// Fuente de verdad de permisos: tabla `profiles` en Supabase.
// USER_COLORS solo se usa para presentación visual.
async function fetchProfile(supabaseUser, accessToken = '') {
  const email    = supabaseUser.email;
  const isProduction = process.env.NODE_ENV === 'production';
  const demoAccount = getDemoAccountByEmail(email);
  const cosmetic = demoAccount ? {
    color: demoAccount.color,
    initials: demoAccount.name.slice(0, 2).toUpperCase(),
    name: demoAccount.name,
    roleLabel: demoAccount.roleLabel,
  } : USER_COLORS[email] || {
    color:     '#5B9BD5',
    initials:  email.slice(0, 2).toUpperCase(),
    name:      supabaseUser.user_metadata?.name || email.split('@')[0],
    roleLabel: 'Colaborador',
  };

  // Intentar leer el rol real desde la tabla profiles con retries antes de bloquear.
  if (isConfigured) {
    let lastError = null;
    for (let attempt = 0; attempt < PROFILE_RETRY_DELAYS.length; attempt += 1) {
      if (PROFILE_RETRY_DELAYS[attempt]) await sleep(PROFILE_RETRY_DELAYS[attempt]);
      try {
        const { data: profile, error } = await readProfileWithTimeout(supabaseUser.id, email, accessToken);

        if (!error && profile) {
          const isClient = profile.role === 'cliente';

          // 🔴 LA FICHA PARTICIPA DE LA DECISION.
          // Antes se retornaba aca con `profiles.role` y nada mas, y el chequeo de acceso
          // cerrado quedaba en la rama de fallback, inalcanzable para un cliente normal.
          // Solo una ficha ENCONTRADA y cerrada niega: la ausencia no bloquea (ver
          // `lib/clientAccess.js`, regla de direccion).
          // 🔴 LA FICHA RESUELTA AQUI VIAJA AL PORTAL. ANTES SE TIRABA.
          //
          // Esta consulta ya existia —por `portal_user_id`, sin pasar por ningun filtro
          // de pipeline— y su resultado se descartaba en cuanto decidia el acceso. El
          // portal, que necesita EL MISMO dato, lo buscaba por su cuenta en
          // `data.clientes`, que el panel filtra con `esFilaDeQA`. Resultado medido el
          // 2026-10-06 sobre un fixture real: un cliente `is_test = true` con su
          // `portal_user_id` correcto, su perfil `cliente` y su acceso activo NUNCA se
          // encontraba a si mismo, y el portal le afirmaba «no encontramos una ficha».
          //
          // Se declara fuera del `if` para que la ficha sobreviva al bloque. Viaja la
          // FILA entera, no sólo su id: el portal la usa como objeto —nombre, correo,
          // servicio— y con medio objeto resolvería a medias.
          //
          // La rama de caché (`profile-cache`) NO la lleva a propósito: es un render
          // provisional con `profileRefreshing: true`, y meter la ficha completa en
          // `localStorage` sería guardar más de lo que hace falta para pintar un
          // esqueleto. En cuanto esta consulta responde, el usuario completo la trae.
          let clienteDelPortal = null;
          if (isClient) {
            const { data: fichaDelPortal } = await readClientPortalIdentityWithTimeout(supabaseUser, accessToken);
            // Segunda consulta SOLO si la primera no encontro ficha: el dueño no paga el costo.
            const porMembresia = decidirAccesoDeCliente(fichaDelPortal) === 'sin_ficha'
              ? (await readClientPortalIdentityByMembershipWithTimeout(supabaseUser, accessToken)).data?.clientes || null
              : null;
            clienteDelPortal = fichaDelPortal || porMembresia || null;
            if (decidirAccesoDeClienteConMembresia(fichaDelPortal, porMembresia) === 'bloqueado') {
              return createProfileLoadErrorUser(
                supabaseUser,
                cosmetic,
                'client-access-blocked',
                'Tu acceso al portal no esta activo. Escríbenos para revisar tu cuenta.'
              );
            }
          }

          const resolved = {
            ...supabaseUser,
            name:      profile.name || cosmetic.name,
            role:      profile.role,
            perms:     profile.role,
            permissions: normalizePermissions(profile.role, profile.permissions),
            roleLabel: ROLE_PRESETS[profile.role]?.label || cosmetic.roleLabel,
            color:     cosmetic.color,
            initials:  cosmetic.initials,
            isClient,
            clientEmail: isClient ? email : null,
            // El portal resuelve SU ficha con esto, no con la lista filtrada del panel.
            clienteDelPortal,
            _source:   'db',
          };
          storeConfirmedProfile(supabaseUser, resolved);
          return resolved;
        }

        const { data: portalCliente } = await readClientPortalIdentityWithTimeout(supabaseUser, accessToken);
        if (portalCliente?.id) {
          const portalUser = createClientPortalUserFromCliente(supabaseUser, portalCliente, cosmetic);
          storeConfirmedProfile(supabaseUser, portalUser);
          return portalUser;
        }
        lastError = error || new Error('Perfil no encontrado');
      } catch (e) {
        lastError = e;
      }
      console.warn(
        `[fetchProfile] Intento ${attempt + 1}/${PROFILE_RETRY_DELAYS.length} sin perfil confirmado:`,
        lastError?.message || 'profiles unavailable'
      );
    }

    if (isProduction) {
      const { data: portalCliente } = await readClientPortalIdentityWithTimeout(supabaseUser, accessToken);
      if (portalCliente?.id) {
        return createClientPortalUserFromCliente(supabaseUser, portalCliente, cosmetic, 'client-portal-final-fallback');
      }
      return createProfileLoadErrorUser(
        supabaseUser,
        cosmetic,
        lastError?.message === 'profiles timeout' ? 'profile-timeout' : 'no-profile',
        lastError?.message || 'No encontramos un perfil interno para esta cuenta.'
      );
    }
  }

  // Si profiles falla o el usuario no tiene perfil en DB:
  // en produccion NO se conceden permisos ni se degrada a portal cliente.
  if (isProduction) {
    // Producción: sin perfil = sin permisos. Solo cosmético.
    return createProfileLoadErrorUser(supabaseUser, cosmetic, 'no-profile', 'No encontramos un perfil interno para esta cuenta.');
  }

  // Desarrollo: fallback por correo para facilitar trabajo local.
  // ESTE BLOQUE NO LLEGA A PRODUCCIÓN.
  const demoRole = getDemoAccountByEmail(email)?.role;
  const devPerms = demoRole
                 || (email === 'david@feriadesign.com'  ? 'admin'
                 : email === 'admin@feria.design'    ? 'admin'
                 : email === 'info@feria.design'     ? 'admin'
                 : email === 'info@davidferia.com'    ? 'cliente'
                 : 'cliente');

  return {
    ...supabaseUser,
    name:      cosmetic.name,
    role:      devPerms,
    perms:     devPerms,
    roleLabel: ROLE_PRESETS[devPerms]?.label || cosmetic.roleLabel,
    permissions: normalizePermissions(devPerms),
    color:     cosmetic.color,
    initials:  cosmetic.initials,
    isClient:  devPerms === 'cliente',
    clientEmail: devPerms === 'cliente' ? email : null,
    _source:   'dev-fallback',
  };
}

// ── PROVIDER ─────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (USE_LOCAL_DEMO_AUTH) {
      const savedDemoEmail = localStorage.getItem(LOCAL_DEMO_AUTH_KEY);
      const savedAccount = getDemoAccountByEmail(savedDemoEmail);
      if (savedAccount) setUser(createDemoUser(savedAccount));
      setLoading(false);
      return;
    }

    if (!isConfigured) {
      console.warn('[AuthContext] Supabase no configurado.');
      setLoading(false);
      return;
    }

    // Aviso de lentitud: no liberar loading aqui. Si hay sesion valida,
    // hacerlo redirige a /login antes de que fetchProfile termine.
    const safetyTimer = setTimeout(() => {
      console.warn('[AuthContext] La carga de perfil esta tardando mas de lo habitual.');
    }, 8000);

    const init = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (session?.user) {
          const cachedProfile = readConfirmedProfileCache(session.user);
          if (cachedProfile) {
            setUser(cachedProfile);
            setLoading(false);
          }

          try {
            const profile = await fetchProfile(session.user, session.access_token);
            setUser(resolveStableProfileResult(profile, session.user, userRef.current));
          } catch (profileErr) {
            // profiles falló — no inventar permisos internos.
            // Si hay perfil confirmado reciente, mantenerlo mientras profiles se recupera.
            console.warn('[AuthContext] fetchProfile falló — sin permisos internos', profileErr.message);
            const fallback = resolveStableProfileResult(
              createProfileLoadErrorUser(session.user, {}, 'profile-fetch-error', profileErr?.message || 'No pudimos cargar tu perfil.'),
              session.user,
              userRef.current
            );
            setUser(fallback || {
              ...session.user,
              name:     session.user.email?.split('@')[0] || 'Usuario',
              role:     'profile_error',
              perms:    'profile_error',
              isClient: false,
              permissions: normalizePermissions('profile_error'),
              profileLoadError: true,
              profileErrorMessage: profileErr?.message || 'No pudimos cargar tu perfil.',
              _source:  'profile-error',
            });
          }
        }
      } catch (err) {
        console.error('[AuthContext] Error en getSession:', err.message);
        setError(err.message);
      } finally {
        clearTimeout(safetyTimer);
        setLoading(false);
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // ── EL EVENTO NO ES RUIDO: ES LA MITAD DE LA DECISION ────────────────────
      // Hasta hoy este parametro se llamaba `_event` y se tiraba. `PASSWORD_RECOVERY`
      // es la senal con la que Supabase distingue «este usuario viene de un enlace de
      // recuperacion» de «este usuario inicio sesion»; ignorarla dejaba las dos
      // situaciones indistinguibles para la app.
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery();
      } else if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        // Un login normal o una salida cierran cualquier recuperacion pendiente:
        // asi una intencion vieja nunca secuestra la pantalla de quien entro bien.
        clearPasswordSetupIntent();
      }
      try {
        if (session?.user) {
          const profile = await fetchProfile(session.user, session.access_token);
          setUser(resolveStableProfileResult(profile, session.user, userRef.current));
        } else {
          setUser(null);
        }
      } catch (err) {
        // profiles falló en evento de auth — mismo criterio: sin permisos internos.
        console.warn('[AuthContext] onAuthStateChange fetchProfile error:', err.message);
        if (session?.user) {
          setUser(resolveStableProfileResult(
            createProfileLoadErrorUser(session.user, {}, 'auth-change-error', err?.message || 'No pudimos cargar tu perfil.'),
            session.user,
            userRef.current
          ));
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    setError(null);
    const demoAccount = getDemoAccountByEmail(email);

    if (USE_LOCAL_DEMO_AUTH && demoAccount?.password === password) {
      localStorage.setItem('feria_demo_mode', 'true');
      localStorage.setItem(LOCAL_DEMO_AUTH_KEY, demoAccount.email);
      setUser(createDemoUser(demoAccount));
      return true;
    }

    if (!isConfigured) {
      setError('Supabase no está configurado. Usa una cuenta demo local o configura las variables REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY.');
      return false;
    }

    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) setError(err.message);
    return !err;
  };

  const signOut = async () => {
    // Salir es la via de escape de la pantalla obligatoria de contrasena nueva.
    // Si la intencion sobreviviera al logout, el guard devolveria a /login para
    // siempre.
    clearPasswordSetupIntent();
    localStorage.removeItem(LOCAL_DEMO_AUTH_KEY);
    if (isConfigured && !USE_LOCAL_DEMO_AUTH) await supabase.auth.signOut();
    setUser(null);
  };

  const retryProfile = async () => {
    if (!isConfigured || USE_LOCAL_DEMO_AUTH) return false;
    setLoading(true);
    setError(null);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) {
        setUser(null);
        return false;
      }
      const profile = await fetchProfile(session.user, session.access_token);
      setUser(resolveStableProfileResult(profile, session.user, userRef.current));
      return !profile?.profileLoadError;
    } catch (err) {
      const message = err?.message || 'No pudimos cargar tu perfil.';
      setError(message);
      const current = userRef.current;
      if (current) {
        setUser(resolveStableProfileResult(
          createProfileLoadErrorUser(current, {}, 'profile-retry-error', message),
          current,
          current
        ));
      } else {
        setUser(null);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Reglas de acceso por módulo — usa presets y permisos personalizados
  const canAccess = (module) => canAccessModule(user, module);

  return (
    <AuthContext.Provider value={{ user, loading, error, signIn, signOut, retryProfile, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
